import React, { useMemo, useState } from 'react';
import { format, differenceInDays } from 'date-fns';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Cell,
} from 'recharts';

const CHART_COLORS = ['#f59e0b', '#10b981', '#6366f1', '#ec4899', '#06b6d4', '#8b5cf6', '#f43f5e', '#14b8a6', '#eab308', '#3b82f6'];

export const ExpenseCharts = ({ visibleExpenses, dateRange }) => {
  const [chartGroupBy, setChartGroupBy] = useState('category');

  const chartData = useMemo(() => {
    const byCategory = {};
    const byEmployee = {};
    visibleExpenses
      .filter((e) => e.status === 'Approved')
      .forEach((e) => {
        byCategory[e.category] = (byCategory[e.category] || 0) + (e.amount || 0);
        const who = e.entered_by || 'Unknown';
        byEmployee[who] = (byEmployee[who] || 0) + (e.amount || 0);
      });
    const toArr = (obj) =>
      Object.entries(obj)
        .map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }))
        .sort((a, b) => b.value - a.value);
    return { byCategory: toArr(byCategory), byEmployee: toArr(byEmployee) };
  }, [visibleExpenses]);

  const trendData = useMemo(() => {
    const approved = visibleExpenses.filter((e) => e.status === 'Approved');
    let granularity = 'monthly';
    if (dateRange.from && dateRange.to) {
      const days = differenceInDays(dateRange.to, dateRange.from) + 1;
      granularity = days > 62 ? 'monthly' : 'daily';
    }
    const buckets = {};
    approved.forEach((e) => {
      const d = new Date(e.date);
      const key = granularity === 'monthly' ? format(d, 'yyyy-MM') : format(d, 'yyyy-MM-dd');
      buckets[key] = (buckets[key] || 0) + (e.amount || 0);
    });
    const data = Object.keys(buckets).sort().map((k) => ({
      bucket: k,
      label: granularity === 'monthly' ? format(new Date(k + '-01'), 'MMM yyyy') : format(new Date(k), 'MMM dd'),
      value: Number(buckets[k].toFixed(2)),
    }));
    return { data, granularity };
  }, [visibleExpenses, dateRange]);

  const barData = chartGroupBy === 'category' ? chartData.byCategory : chartData.byEmployee;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4" data-testid="expense-chart-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Approved Expenses Breakdown</h3>
            <p className="text-xs text-gray-500">{dateRange.label} · only approved expenses</p>
          </div>
          <div className="flex gap-2" data-testid="chart-group-toggle">
            <button
              onClick={() => setChartGroupBy('category')}
              data-testid="chart-by-category"
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${
                chartGroupBy === 'category'
                  ? 'bg-amber-500 text-white shadow'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              By Category
            </button>
            <button
              onClick={() => setChartGroupBy('employee')}
              data-testid="chart-by-employee"
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${
                chartGroupBy === 'employee'
                  ? 'bg-amber-500 text-white shadow'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              By Employee
            </button>
          </div>
        </div>
        {barData.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-sm text-gray-500">
            No approved expenses in this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={barData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" angle={-20} textAnchor="end" height={60} interval={0} tick={{ fontSize: 12, fill: '#4b5563' }} />
              <YAxis tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}`} tick={{ fontSize: 12, fill: '#4b5563' }} />
              <Tooltip
                formatter={(v) => [`₹${Number(v).toFixed(2)}`, 'Approved']}
                cursor={{ fill: 'rgba(245, 158, 11, 0.08)' }}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {barData.map((entry, idx) => (
                  <Cell key={entry.name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4" data-testid="expense-trend-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Expense Trend</h3>
            <p className="text-xs text-gray-500">
              {dateRange.label} · {trendData.granularity === 'monthly' ? 'monthly' : 'daily'} · approved only
            </p>
          </div>
        </div>
        {trendData.data.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-sm text-gray-500">
            No approved expenses in this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trendData.data} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" angle={-20} textAnchor="end" height={60} interval="preserveStartEnd" tick={{ fontSize: 12, fill: '#4b5563' }} />
              <YAxis tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}`} tick={{ fontSize: 12, fill: '#4b5563' }} />
              <Tooltip
                formatter={(v) => [`₹${Number(v).toFixed(2)}`, 'Approved']}
                labelFormatter={(l) => l}
                cursor={{ stroke: '#f59e0b', strokeWidth: 1, strokeDasharray: '3 3' }}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <Line type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4, fill: '#f59e0b', strokeWidth: 0 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
